import { useEffect, useState } from 'react';

/**
 * Light or dark, chosen by the person rather than their operating system.
 *
 * One copy for every surface. There were four byte-identical ones, which is
 * how a fix reaches the console a fortnight before it reaches the app people
 * actually use.
 *
 * -- Why light is the default --------------------------------------------
 * This used to follow `prefers-color-scheme`, so a phone set to dark handed a
 * 74-year-old white text on near-black. That is the wrong default here.
 * Cataracts scatter light, and scattered light around bright glyphs on a dark
 * field is halation: the letters bloom into each other and the edges go soft.
 * The people most likely to have a phone in dark mode are the ones who handed
 * it to their grandchild to set up.
 *
 * So the default is light for everybody, and anybody who wants dark can have
 * it in one tap, remembered.
 *
 * -- Why the initial value is read from the DOM ----------------------------
 * `applyStoredTheme` runs in the page head, before React exists, so the
 * attribute is already right by the time this mounts. Reading localStorage
 * again here would be a second source of truth that can disagree with what is
 * on screen.
 */
export const THEME_KEY = 'drbell.theme';

export type Theme = 'light' | 'dark';

export function useTheme(): [Theme, (next: Theme) => void] {
  const [theme, setTheme] = useState<Theme>(
    () => (document.documentElement.dataset.theme === 'dark' ? 'dark' : 'light'),
  );

  useEffect(() => {
    document.documentElement.dataset.theme = theme;

    /*
     * The browser's own chrome, kept in step.
     *
     * `color-scheme` in CSS makes the native controls follow data-theme, but
     * the address-bar colour is a <meta> and CSS cannot reach it. Without
     * this, choosing dark left an orange bar above a near-black page.
     */
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', theme === 'dark' ? '#0d0f13' : '#025158');

    try {
      localStorage.setItem(THEME_KEY, theme);
    } catch {
      // Private browsing, or storage disabled. The choice still applies for
      // this visit; it simply will not be remembered, which is better than
      // throwing on a preference toggle.
    }
  }, [theme]);

  return [theme, setTheme];
}

export function ThemeToggle({ className = '' }: { className?: string }) {
  const [theme, setTheme] = useTheme();
  const next = theme === 'dark' ? 'light' : 'dark';

  return (
    <button
      type="button"
      onClick={() => setTheme(next)}
      aria-label={`Switch to ${next} mode`}
      title={`Switch to ${next} mode`}
      className={`flex min-h-11 min-w-11 items-center justify-center rounded-lg border border-(--line) px-3 text-sm hover:border-(--accent) ${className}`}
    >
      {/* A word as well as the glyph. A lone sun or moon is a guess for
          anybody who has not met the convention, and this product's readers
          are disproportionately in that group. */}
      <span aria-hidden="true">{theme === 'dark' ? '☀' : '☾'}</span>
      <span className="ml-2 hidden sm:inline">{next === 'dark' ? 'Dark' : 'Light'}</span>
    </button>
  );
}
